// How a payment option reads to the family — its name, its order among
// its siblings, and the line saying what's due today. Keyed off the
// option's period and membership scenario (`payment_period_oap`), never
// its slug, so an option the server starts offering reads correctly
// without a client change.

import type { QuotePaymentOption } from "./api";

type OptionShape = Pick<
  QuotePaymentOption,
  "payment_period" | "payment_period_oap" | "due_today_cents" | "term_months"
>;

const usd = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

export function dollars(cents: number): string {
  return usd.format(cents / 100);
}

export function termLabel(termMonths: number | null): string {
  if (termMonths === null) return "";
  if (termMonths % 12 === 0) return `${termMonths / 12} years`;
  return `${termMonths} months`;
}

/** Whether the membership fee is folded into the monthly premium — the
 * no-money-down scenario, where nothing is collected at signing and no
 * membership charge or installment ever happens on its own. */
export function membershipInMonthly(option: {
  payment_period_oap: string | null;
}): boolean {
  return option.payment_period_oap === "no_money_down";
}

/** Whether part of the membership fee is paid today and the rest folded
 * into the monthly premium — the partial-down scenario. Only an agent
 * chooses it, and the server neither offers it nor finalizes a quote on
 * it; named here so an option or selection carrying it never reads as the
 * membership paid in full today. */
export function membershipPartlyInMonthly(option: {
  payment_period_oap: string | null;
}): boolean {
  return option.payment_period_oap === "partial_down";
}

export function optionLabel(option: OptionShape): string {
  if (option.payment_period === "single_pay") return "Pay in full";
  if (option.payment_period_oap === "installment_3") {
    return "Monthly + membership installments";
  }
  if (membershipInMonthly(option)) {
    const term = termLabel(option.term_months);
    return term ? `Nothing down · ${term}` : "Nothing down";
  }
  if (membershipPartlyInMonthly(option)) {
    const term = termLabel(option.term_months);
    return term ? `Partial down · ${term}` : "Partial down";
  }
  if (option.payment_period === "3_year_pay") return "Monthly · 3 years";
  if (option.payment_period === "10_year_pay") return "Monthly · 10 years";
  return "Pay monthly";
}

/** Within one term: membership paid today, then in installments, then
 * partly and then wholly folded into the monthly payment. */
function scenarioRank(option: OptionShape): number {
  if (option.payment_period_oap === "installment_3") return 1;
  if (membershipPartlyInMonthly(option)) return 2;
  if (membershipInMonthly(option)) return 3;
  return 0;
}

/** The display order: pay in full first, then shortest term first, then
 * by membership scenario. `defaultPaymentOption` takes the first monthly
 * option in this order, so an upfront-membership option stays the
 * default over a no-money-down one of the same term. */
export function compareOptions(a: OptionShape, b: OptionShape): number {
  const single = (o: OptionShape) =>
    o.payment_period === "single_pay" ? 0 : 1;
  if (single(a) !== single(b)) return single(a) - single(b);
  const term = (o: OptionShape) => o.term_months ?? 0;
  if (term(a) !== term(b)) return term(a) - term(b);
  return scenarioRank(a) - scenarioRank(b);
}

/** The line under a monthly option's price: what's due today, and how
 * many payments follow. */
export function monthlyTermsLine(option: OptionShape): string {
  const payments = option.term_months;
  if (membershipInMonthly(option)) {
    return payments
      ? `${dollars(0)} today · membership included in ${payments} monthly payments`
      : `${dollars(0)} today · membership included in your monthly payment`;
  }
  if (membershipPartlyInMonthly(option)) {
    const down = `${dollars(option.due_today_cents)} down today`;
    return payments
      ? `${down} · rest of membership included in ${payments} monthly payments`
      : `${down} · rest of membership included in your monthly payment`;
  }
  const today =
    option.due_today_cents > 0
      ? `${dollars(option.due_today_cents)} today for your membership`
      : "Nothing due today";
  return payments ? `${today} · then ${payments} monthly payments` : today;
}

/** The detail line on a payment tile: what's paid today, and where the
 * membership goes. */
export function payTileDetail(
  option: Pick<
    QuotePaymentOption,
    "payment_period_oap" | "due_today_cents" | "recurring_cents" | "term_months"
  >,
): string {
  if (option.recurring_cents === null) {
    return "Paid once today. Nothing else to pay, ever.";
  }
  const payments = option.term_months ?? "—";
  if (membershipInMonthly(option)) {
    return `Nothing down — your membership is included in ${payments} monthly payments.`;
  }
  if (membershipPartlyInMonthly(option)) {
    return `${dollars(option.due_today_cents)} down today — the rest of your membership is included in ${payments} monthly payments.`;
  }
  if (option.due_today_cents > 0) {
    return `${dollars(option.due_today_cents)} today for your membership, then ${payments} monthly payments.`;
  }
  return `Nothing down — ${payments} monthly payments.`;
}

/** A monthly amount as the recap rows state it, naming the membership
 * when all or part of it rides in the payment. */
export function monthlyScheduleValue(
  option: Pick<QuotePaymentOption, "payment_period_oap" | "term_months">,
  recurringCents: number,
): string {
  const base = `${dollars(recurringCents)}/mo for ${option.term_months ?? "—"} months`;
  if (membershipInMonthly(option)) return `${base}, membership included`;
  if (membershipPartlyInMonthly(option)) {
    return `${base}, rest of membership included`;
  }
  return base;
}

/** The note under a monthly selection's charged-today amount on the
 * finalize screen: what today's charge pays for, and where any of the
 * membership not paid today goes. */
export function finalizeDueNote(
  option: { payment_period_oap: string | null },
  dueCents: number,
  plan: string,
): string {
  if (membershipInMonthly(option)) {
    return `Nothing due now. Your ${plan} Membership is included in your monthly payments, which start next month.`;
  }
  if (membershipPartlyInMonthly(option)) {
    return `${dollars(dueCents)} down today toward your ${plan} Membership. The rest of it is included in your monthly payments, which start next month.`;
  }
  return dueCents === 0
    ? "Nothing due now. Your monthly payments start next month."
    : `Enrolls you in the ${plan} Membership, which locks in today's prices and covers you anywhere in the U.S.`;
}
